import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useI18n, fill } from "@/i18n";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { regionBySlug, REGIONS } from "@/data/regions";
import { orgSchema, breadcrumb, faqSchema } from "@/lib/seo";
import { ArrowLeft, MapPin, ArrowRight } from "@phosphor-icons/react";

export default function RegionDetail() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const region = regionBySlug(slug);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!region) { navigate("/regionen"); return; }
    api.get("/cities").then((r) => setCities(r.data.filter((c) => c.canton === region.code)));
  }, [slug]); // eslint-disable-line

  if (!region) return null;
  const c = region[lang] || region.de;
  const title = lang === "de"
    ? `Flughafentransfer Kanton ${c.name} ab Zürich (ZRH) | Festpreis`
    : `Airport Transfer to ${c.name} from Zürich (ZRH) | Fixed Fare`;
  const faqs = [
    { q: fill(t("reg_faq1_q"), { name: c.name }), a: fill(t("reg_faq1_a"), { name: c.name }) },
    { q: fill(t("reg_faq2_q"), { name: c.name }), a: fill(t("reg_faq2_a"), { name: c.name }) },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="region-detail-page">
      <Seo title={title} description={c.intro} path={`/regionen/${region.slug}`}
        jsonLd={[orgSchema(),
          breadcrumb([{ name: "Home", path: "/" }, { name: t("nav_regions"), path: "/regionen" }, { name: c.name, path: `/regionen/${region.slug}` }]),
          faqSchema(faqs)]} />

      <Link to="/regionen" className="inline-flex items-center gap-2 font-body text-sm text-[#5A607A] hover:text-[#0B1533] transition-colors duration-200 mb-6"><ArrowLeft size={15} /> {t("regions_all")}</Link>
      <span className="font-body text-xs font-semibold text-[#EF5A1E] bg-[#EEF1FF] rounded-full px-3 py-1">{region.code}</span>
      <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl text-[#0B1533] leading-tight mt-3">{fill(t("reg_h1"), { name: c.name })}</h1>
      <p className="font-body text-lg text-[#3A3F55] leading-relaxed mt-6 max-w-3xl">{c.intro}</p>

      <h2 className="font-heading font-bold text-2xl tracking-tight text-[#0B1533] mt-12 mb-5">{cities.length} {t("region_destinations")}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {cities.map((ct) => (
          <Link key={ct.id} to={`/destinations/${ct.id}`} data-testid={`region-city-${ct.id}`}
            className="group flex items-center justify-between gap-3 rounded-2xl border border-[#E9E5DE] bg-white px-5 py-4 hover:border-[#EF5A1E] transition-colors duration-200">
            <span className="flex items-center gap-2.5 font-body text-sm text-[#0B1533]"><MapPin size={16} className="text-[#EF5A1E]" /> {ct.name}</span>
            <ArrowRight size={16} className="text-[#9CA0B0] group-hover:text-[#EF5A1E] group-hover:translate-x-1 transition-all duration-200" />
          </Link>
        ))}
      </div>

      <div className="mt-12">
        <h2 className="font-heading font-bold text-2xl tracking-tight text-[#0B1533] mb-5">{t("dfaq_h2")}</h2>
        <div className="space-y-4">
          {faqs.map((f, i) => (
            <div key={i} className="rounded-2xl border border-[#E9E5DE] bg-white p-5">
              <p className="font-body font-semibold text-[#0B1533]">{f.q}</p>
              <p className="font-body text-sm text-[#5A607A] mt-2 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
